import omitBy from "lodash/omitBy";

import {
  ADD_PLAID_ACCESS_TOKEN,
  SET_SPLITWISE_LOADED,
  SET_SPLITWISE_ERROR,
  SET_SPLITWISE_USER,
  SET_SPLITWISE_FRIENDS,
  SET_SPLITWISE_GROUPS,
  SET_SPLITWISE_EXPENSE_ERROR,
  SET_STRIPE_DATA,
} from "./actionTypes";

import firebase from "../services/firebase";
import api from "../services/api";
import trackingService from "../services/tracking";

const getUserFromState = (state) => state.firebase.auth;

export const fetchExternalData = () => (dispatch, getState) => {
  dispatch(fetchSplitwiseData());
  dispatch(fetchStripeData());
};

export const deleteSplitwiseToken = () => (dispatch, getState) => {
  const user = getUserFromState(getState());
  if (user) {
    firebase
      .firestore()
      .doc(`users/${user.uid}`)
      .set({ splitwiseToken: null }, { merge: true })
      .catch((error) =>
        console.log("error deleting setting splitwise token: ", error)
      );
  }
};

export const writeSplitwiseToken = (splitwiseToken) => (dispatch, getState) => {
  const user = getUserFromState(getState());
  trackingService.logEvent("write_splitwise_token", { user });
  if (user) {
    firebase
      .firestore()
      .doc(`users/${user.uid}`)
      .set({ splitwiseToken }, { merge: true })
      .catch((error) => console.log("error setting splitwise token: ", error));
  }
};

export const addPlaidAccessToken = (accessToken) => ({
  type: ADD_PLAID_ACCESS_TOKEN,
  payload: {
    accessToken,
  },
});
// TODO: this should be moved to the server
export const exchangePlaidToken = (public_token, metadata) => (
  dispatch,
  getState
) => {
  if (!api.isProd())
    firebase.functions().useFunctionsEmulator(api.getFirebaseLocalHost());
  trackingService.logEvent("exchange_plaid_token", { metadata });
  const plaidExchangeToken = firebase
    .functions()
    .httpsCallable("plaidExchangeToken");
  plaidExchangeToken({
    public_token,
    metadata,
  })
    .then((result) => console.log("success result: ", result))
    .catch((error) => console.log("error: ", error));
};

export const writePlaidAccessToken = (tokenResponse, metadata) => (
  dispatch,
  getState
) => {
  const uid = getUserFromState(getState()).uid;
  return firebase
    .firestore()
    .doc(`users/${uid}`)
    .set(
      {
        plaidTokens: firebase.firestore.FieldValue.arrayUnion({
          access_token: tokenResponse.access_token,
          item_id: tokenResponse.item_id,
          metadata,
        }),
      },
      { merge: true }
    );
};

export const fetchPlaidTransactions = () => (dispatch, state) => {
  const plaidFetchTransactions = firebase
    .functions()
    .httpsCallable("plaidFetchTransactions");
  plaidFetchTransactions()
    .then((result) => console.log("success result: ", result))
    .catch((error) => console.log("error: ", error));
};

const setSplitwiseUser = (user) => ({
  type: SET_SPLITWISE_USER,
  payload: { user },
});

const setSplitwiseFriends = (friends) => ({
  type: SET_SPLITWISE_FRIENDS,
  payload: { friends },
});

const setSplitwiseGroups = (groups) => ({
  type: SET_SPLITWISE_GROUPS,
  payload: { groups },
});

const setSplitwiseLoaded = () => ({
  type: SET_SPLITWISE_LOADED,
});
const setSplitwiseError = (error) => ({
  type: SET_SPLITWISE_ERROR,
  payload: { error },
});
const setSplitwiseExpenseError = (error) => ({
  type: SET_SPLITWISE_EXPENSE_ERROR,
  payload: { error },
});

const getSplitwiseTokenFromState = (state) => {
  return (
    state &&
    state.firebase &&
    state.firebase.profile &&
    state.firebase.profile.splitwiseToken
  );
};

const fetchSplitwiseData = () => (dispatch, getState) => {
  const token = getSplitwiseTokenFromState(getState());
  if (token) {
    const promises = [];
    promises.push(
      hitSplitwiseProxy(token, "get", "get_current_user").then((result) =>
        dispatch(setSplitwiseUser(result.user))
      ),
      hitSplitwiseProxy(token, "get", "get_friends").then((result) =>
        dispatch(
          setSplitwiseFriends(
            result.friends.sort(
              (a, b) => b.updated_at.localeCompare(a.updated_at) // sort by `updated_at` desc so most recently used friends are first
            )
          )
        )
      ),
      hitSplitwiseProxy(token, "get", "get_groups").then((result) =>
        dispatch(
          setSplitwiseGroups(
            result.groups
              .filter((g) => g.id !== 0)
              .sort(
                (a, b) => b.updated_at.localeCompare(a.updated_at) // sort by `updated_at` desc so most recently used friends are first
              )
          )
        )
      ) /*,
        hitSplitwiseProxy(token, "get", "get_groups").then(result =>
          dispatch(setSplitwiseGroups(result.groups))
        )*/
    );
    Promise.all(promises)
      .then(() => dispatch(setSplitwiseLoaded()))
      .catch((error) => dispatch(setSplitwiseError(error)));
  }
};

export const initSplitwiseExpense = ({ transaction, options }) => (
  dispatch,
  getState
) => {
  console.log("initSplitwiseExpense thunk");
  const splitwiseToken = getSplitwiseTokenFromState(getState());
  if (splitwiseToken) {
    trackingService.logEvent("create_splitwise_expense", {
      transaction,
      options,
    });
    const initSplitwiseExpense = firebase
      .functions()
      .httpsCallable("initSplitwiseExpense");
    initSplitwiseExpense({
      splitwiseToken, // secrisk?
      transaction,
      options,
    })
      .then((result) => console.log("success result: ", result))
      .catch((error) => console.log("error: ", error));
  }
};

const getSplitwiseExpensesFromState = (state) =>
  state && state.firebase.profile && state.firebase.profile.expenses;

export const deleteSplitwiseExpense = (expense) => (dispatch, getState) => {
  const oldExpenses = getSplitwiseExpensesFromState(getState());
  const expenses = omitBy(oldExpenses, (exp) => exp && exp.id === expense.id);
  return firebase
    .firestore()
    .doc(`users/${getState().firebase.auth.uid}`)
    .update({ expenses })
    .catch((error) => console.log("error deleting splitwise expense: ", error));
};

const firebaseUpdateExpenses = (expenses, state) => {
  const uid = state.firebase.auth.uid;
  return firebase
    .firestore()
    .doc(`users/${uid}`)
    .set(
      {
        expenses,
      },
      { merge: true }
    )
    .catch((error) =>
      console.log("error setting splitwise expenses user data: ", error)
    );
};

const writeSplitwiseExpense = (expense, transaction) => (
  dispatch,
  getState
) => {
  //expenses are keyed on the transactionId from which they were generated.
  return firebaseUpdateExpenses(
    {
      [transaction.transaction_id]: expense,
    },
    getState()
  );
};

export const initDeleteSplitwiseExpense = (expense) => (dispatch, getState) => {
  const token = getSplitwiseTokenFromState(getState());
  trackingService.logEvent("delete_splitwise_expense", { expense });

  return hitSplitwiseProxy(token, "post", "delete_expense/" + expense.id).then(
    (result) => dispatch(deleteSplitwiseExpense(expense)),
    (error) => console.log("error deleting splitwise expense")
  );
};

const hitSplitwiseProxy = (token, method, apiMethod, data = null) => {
  let headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const init = {
    method,
    headers,
  };
  if (data) init.body = JSON.stringify(data);
  return fetch(
    api.getBaseUrl() + `proxy?method=${apiMethod}`,
    init
  ).then((response) => response.json());
};

const getSubscriptionIdFromState = (state) =>
  state.firebase &&
  state.firebase.profile &&
  state.firebase.profile.subscription &&
  state.firebase.profile.subscription.id;

export const fetchStripeData = () => async (dispatch, getState) => {
  const subscriptionId = getSubscriptionIdFromState(getState());
  if (subscriptionId) {
    const stripeGetInfoForSubscription = firebase
      .functions()
      .httpsCallable("stripeGetInfoForSubscription");
    const { data } = await stripeGetInfoForSubscription({ subscriptionId });
    return dispatch(setStripeData(data));
  } else return;
};

const setStripeData = (data) => ({
  type: SET_STRIPE_DATA,
  payload: { ...data },
});
